import React, {useState,useEffect} from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Menu,
  MenuItem,
  Button,
  FormGroup,
  FormControlLabel,
  createTheme,
  ThemeProvider,
  StyledEngineProvider,
} from '@mui/material';
import { AccountCircle } from '@mui/icons-material';
import StatusBadge from './StatusBadge';
import Oauth from '../../utils/Oauth';
import AboutModal from '../AboutModal';
import ImpersonationModal from '../ImpersonationModal';
import SessionConstants from '../../config/SessionConstants';
import Switch from '@mui/material/Switch';
import { alpha, styled } from '@mui/material/styles';
import './index.css';

export default function UserMenu(props) {
  
  const [ aboutModalIsOpen, setAboutModal ] = useState(false);
  const [ impersonationModalIsOpen, setImpersonationModal ] = useState(false);
  const [anchorEl, setAnchorEl ] = useState(null);
  const [statusFeed, setStatusFeed] = useState(props.message);
  const [isImpersonating, setIsImpersonating] = useState(sessionStorage.getItem(SessionConstants.IS_IMPERSONATING) === 'true');
  // Use original user's sysadmin status if impersonating to keep impersonation button visible
  const [isSysAdmin, setIsSysAdmin] = useState(
    isImpersonating 
      ? sessionStorage.getItem(SessionConstants.ORIGINAL_IS_SYSADMIN) === 'true'
      : sessionStorage.getItem(SessionConstants.IS_SYSADMIN) === 'true'
  );
  // Use current (impersonated) user's manager status to show appropriate menu items
  const [isManager, setIsManager] = useState(sessionStorage.getItem(SessionConstants.IS_MANAGER) === 'true');

  // Add this useEffect to update statusFeed when props.message changes
  useEffect(() => {
    setStatusFeed(props.message);
  }, [props.message]);

  const clearStatusFeed = () => {
    setStatusFeed(null);
    handleClose();
  }
  const theme = createTheme();

  const toggle = previous => !previous;

  const closeModal =()=>{
    setAboutModal(false);
  }

  const handleAboutClick =()=> {
    setAboutModal(toggle);
    setAnchorEl(null);
  } 
 const handleImpersonationClick =()=> {
    setImpersonationModal(toggle);
    setAnchorEl(null);
  } 
  const handleAboutKeypress = e => {
    if (e.key === "Enter") {
      handleAboutClick();
    }
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const [checked, setChecked] = useState(JSON.parse(sessionStorage.getItem(SessionConstants.FULLSCREEN)));

  const handleSwitchClick = (e) => {
    e.preventDefault();
    if (checked == true) {
      sessionStorage.setItem(SessionConstants.FULLSCREEN, false);
      setChecked(false);
    } else {
      sessionStorage.setItem(SessionConstants.FULLSCREEN, true);
      setChecked(true);
    }
    window.location.reload(false);
  };

  const handleSwitchKeypress = e => {
    if (e.key === "Enter" || e.key === " ") {
      handleSwitchClick(e);
    }
  };
 
  const PrimarySwitch = styled(Switch)(({ theme }) => ({
    '& .MuiSwitch-switchBase.Mui-checked': {
      color: '#006778',
      '&:hover': {
        backgroundColor: alpha('#006778', theme.palette.action.hoverOpacity),
      },
    },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
      backgroundColor: '#006778',
    },
  }));

  const clearTokens = () => {
    Oauth.clearAllSessionVariables();
    sessionStorage.setItem(SessionConstants.ERROR_MESSAGE, 'Logout');
    sessionStorage.setItem(SessionConstants.ERROR_DESCRIPTION, 'Successfully logged out of ED3N');
    sessionStorage.setItem(SessionConstants.LOGOUT_STATUS, true);
  }

  const handleLogout = async () => {
    //  Capture the dev flag before clearTokens wipes it so performLogout can skip the SAMS
    //  logout for a dev login (which never authenticated through SAMS).
    const wasDevLogin = sessionStorage.getItem(SessionConstants.DEVLOGIN) === 'true';
    await clearTokens();
    // console.log("_______------logout: " + SessionConstants.LOGOUT_STATUS);
    Oauth.performLogout(wasDevLogin);
  }

  const handleLogoutKeypress = e => {
    if (e.key === "Enter") {
      handleLogout();
    }
  };

  return (
    <div className="userInfo">
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <Button 
            id="userMenuButton"
            className="userInfoButton" 
            aria-controls="user-menu" 
            aria-haspopup="true" 
            alt="User Menu" 
            aria-label="User Menu" 
            onClick={handleClick}
            // improve button fit in fullscreen mode
            style={{
              minWidth: "0px",
              marginRight: "2px"
            }}
          >
            {statusFeed != null && statusFeed.Status ? (
              <div className="dashProfileIcon" style={{ cursor: 'pointer' }}>
                <StatusBadge status={statusFeed.Status.toLowerCase()} />
              </div>
            ) : <AccountCircle style={{ fontSize: '2rem' }} />}
           
          </Button>
  

        </ThemeProvider>
      </StyledEngineProvider>
      <Menu
        container={props.menuContainerRef?.current || undefined}
        id="user-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem className="userAccountMenuItem" disabled>{sessionStorage.getItem(SessionConstants.CURRENT_USER)}</MenuItem> 
        <MenuItem className="userAccountMenuItem" disabled divider={!isSysAdmin}>NBS Program: {sessionStorage.getItem(SessionConstants.NBS_PROGRAM)}</MenuItem>
        {isSysAdmin && (
          <MenuItem
            className="userAccountMenuItem"
            onClick={handleImpersonationClick}
            aria-label={isImpersonating ? `Impersonating ${sessionStorage.getItem(SessionConstants.USER_FIRSTNAME)} ${sessionStorage.getItem(SessionConstants.USER_LASTNAME)}` : 'Impersonation'}
            divider={true}
          >
           {isImpersonating ? `Impersonating ${sessionStorage.getItem(SessionConstants.USER_FIRSTNAME)} ${sessionStorage.getItem(SessionConstants.USER_LASTNAME)}` : 'Impersonation'}
          </MenuItem>
        )}
        {(isManager) && 
          <>
          <MenuItem
            component={RouterLink}
            to="/programdashboard"
            state={{
                programId: sessionStorage.getItem(SessionConstants.PROGRAM_ID),
              }}
            aria-label="Program Dashboard"
            onClick={handleClose}>
            Program Dashboard
          </MenuItem>
            <MenuItem
            component={RouterLink}
            to="/usermanagement"
            state={{
                programId: sessionStorage.getItem(SessionConstants.PROGRAM_ID),
              }}
            aria-label="User Management"
            onClick={handleClose}
            divider={true}>
            User Management
          </MenuItem>
          </>
        }
         <MenuItem
            component={RouterLink}
            to="/userdashboard"
            state={{
              programId: sessionStorage.getItem(SessionConstants.PROGRAM_ID),
            }}
            aria-label="My Dashboard"
            onClick={handleClose}>
            My Dashboard
          </MenuItem>
          <MenuItem
            component={RouterLink}
            to="/workflowruns"
            state={{
              programId: sessionStorage.getItem(SessionConstants.PROGRAM_ID),
            }}
            aria-label="Workflow Runs"
            onClick={clearStatusFeed}
            divider={true}>
            Workflow Runs
          </MenuItem>
        <MenuItem
          onClick={handleSwitchClick}
          onKeyPress={handleSwitchKeypress}
          aria-label="Fullscreen Toggle"
          divider={true}
        >
          <FormGroup>
            <FormControlLabel className="fullscreenToggle" control={
              <PrimarySwitch
                checked={checked}
                
                inputProps={{ 'aria-label': 'controlled' }}
              />
            } label="Fullscreen" labelPlacement="start" />
          </FormGroup>
        </MenuItem>
        <MenuItem 
          onClick={handleAboutClick}
          onKeyPress={handleAboutKeypress}
          aria-label="About"
          divider={true} >
          About 
        </MenuItem>
        <MenuItem
          onClick={handleLogout}
          onKeyPress={handleLogoutKeypress}
          aria-label="Sign Out" >
          Sign Out
        </MenuItem>
      </Menu>
      <AboutModal  closeModal={closeModal}  modalIsOpen={Boolean(aboutModalIsOpen)} />
      {isSysAdmin && 
            <ImpersonationModal modalIsOpen={impersonationModalIsOpen} closeModal={() => setImpersonationModal(false)} />

      }
    </div>
  );
}

